import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MapPin, Clock, Globe, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const languages = [
  { code: "fr", label: "FR", name: "Français" },
  { code: "en", label: "EN", name: "English" },
  { code: "it", label: "IT", name: "Italiano" },
  { code: "es", label: "ES", name: "Español" },
];

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/menu", label: t("nav.menu") },
    { to: "/restaurant", label: t("nav.restaurant") },
    { to: "/events", label: t("nav.events") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 nav-glass ${
        isScrolled
          ? "bg-background/95 shadow-sm border-b border-border/30"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="/images/logo/logo.svg"
              alt="Brasserie Carougeoise"
              className="h-12 w-auto"
            />
            <span className="hidden sm:block font-heading text-lg font-semibold text-primary">
              Brasserie Carougeoise
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm uppercase tracking-widest transition-colors accent-underline ${
                  location.pathname === link.to
                    ? "text-primary font-medium"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-muted-foreground hover:text-primary"
                  data-testid="language-selector"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm">{currentLang.label}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? "bg-secondary" : ""}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Reservation Button */}
            <Link to="/reservation">
              <Button
                className="bg-primary text-white hover:bg-primary/90 rounded-none px-6 py-5 text-sm tracking-wider uppercase btn-hover-expand"
                data-testid="nav-reserve-btn"
              >
                {t("nav.reservation")}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-primary"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-t border-border/30"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={link.to}
                      className={`block py-2 text-lg font-heading ${
                        location.pathname === link.to
                          ? "text-primary"
                          : "text-muted-foreground"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}

                {/* Mobile Language Selector */}
                <div className="flex gap-2 py-4 border-t border-border/30">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      className={`px-3 py-1 text-sm rounded ${
                        i18n.language === lang.code
                          ? "bg-primary text-white"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>

                <Link to="/reservation" className="mt-2">
                  <Button className="w-full bg-primary text-white rounded-none py-6 text-sm tracking-wider uppercase">
                    {t("nav.reservation")}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-white" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img
              src="/images/logo/logo.svg"
              alt="Brasserie Carougeoise"
              className="h-16 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/70 font-body text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg mb-4">{t("contact.title")}</h4>
            <div className="space-y-3 text-white/70 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-gold" />
                <span>Rue des Noirettes 32<br />1227 Carouge, Genève</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gold" />
                <a href="tel:+41223424243" className="hover:text-white transition-colors">
                  {t("common.phone_display")}
                </a>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-heading text-lg mb-4">{t("reservation.hours_title")}</h4>
            <div className="space-y-2 text-white/70 text-sm">
              <div className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-1 text-gold" />
                <div>
                  <p>Lun - Ven: 6h00 - 23h00</p>
                  <p>Sam - Dim: Fermé</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg mb-4">Navigation</h4>
            <div className="space-y-2 text-sm">
              <Link to="/menu" className="block text-white/70 hover:text-white transition-colors">
                {t("nav.menu")}
              </Link>
              <Link to="/reservation" className="block text-white/70 hover:text-white transition-colors">
                {t("nav.reservation")}
              </Link>
              <Link to="/events" className="block text-white/70 hover:text-white transition-colors">
                {t("nav.events")}
              </Link>
              <Link to="/contact" className="block text-white/70 hover:text-white transition-colors">
                {t("nav.contact")}
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
          <p>© {currentYear} Brasserie Carougeoise. {t("footer.rights")}.</p>
          <a href="#" className="hover:text-white transition-colors">
            {t("footer.legal")}
          </a>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
